import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tab,
  Tabs,
  CircularProgress,
  Alert
} from '@mui/material';
import { grey, blue, red } from '@mui/material/colors';
import { useNavigate, useParams } from 'react-router-dom';
import UICard, { UICardContent, UICardHeading } from '@/components/Shared/Utils/UICard';
import Utils from '@/components/Shared/Utils';
import { LoadingBackdrop } from '@/components/Layout/DefaultLayout';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useResellerContext } from '../../utils/contexts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function CustomerDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { sidebar, selectedCustomer, loading, error, setCustomerId, refreshCustomer } = useResellerContext();
  const [tabValue, setTabValue] = useState(0);
  const [openSuspendDialog, setOpenSuspendDialog] = useState(false);
  
  // Set the customer ID when component mounts or ID changes
  useEffect(() => {
    if (id) {
      setCustomerId(id);
    } else {
      navigate('/resellers/customers');
    }
  }, [id, setCustomerId, navigate]);
  
  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditCustomer = () => {
    navigate(`/resellers/customers/edit/${id}`);
  };

  const handleOpenSuspendDialog = () => {
    setOpenSuspendDialog(true);
  };

  const handleCloseSuspendDialog = () => {
    setOpenSuspendDialog(false);
  };

  const handleSuspendCustomer = () => {
    // API call would go here
    console.log('Suspending customer:', id);
    setOpenSuspendDialog(false);
    // After API call, navigate back or refresh
    refreshCustomer();
  };

  return (
    <Grid container>
      <Utils.LayoutUtils.MainSidebar {...sidebar} />
      <Utils.LayoutUtils.MainContainer background="white">
        <React.Suspense fallback={<LoadingBackdrop />}>
          <Utils.LayoutUtils.MainWrapper>
            {/* Breadcrumbs */}
            <Box mb={3}>
              <Utils.LayoutUtils.MainBreadcrumbs
                paths={[
                  { name: 'Reseller Dashboard', path: '/resellers' },
                  { name: 'Customers', path: '/resellers/customers' },
                  { name: selectedCustomer?.name || 'Customer Details', path: '' }
                ]}
              />
            </Box>
            
            {/* Error State */}
            {error?.selectedCustomer && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.selectedCustomer} <Button size="small" onClick={refreshCustomer}>Retry</Button>
              </Alert>
            )}
            
            {/* Loading State */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : selectedCustomer ? (
              <>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" mb={4}>
                  <Box my="auto">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Utils.LayoutUtils.MainHeading
                        title={selectedCustomer.name}
                        description={`Customer ID: ${selectedCustomer.id}`}
                      />
                      <Chip 
                        label={selectedCustomer.status} 
                        size="small" 
                        color={
                          selectedCustomer.status === 'Active' ? 'success' : 
                          selectedCustomer.status === 'Pending' ? 'warning' : 
                          'error'
                        }
                        sx={{ fontSize: 12 }}
                      />
                    </Stack>
                  </Box>
                  <Stack my="auto" direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEditCustomer}
                      sx={{ 
                        fontWeight: 500,
                        fontSize: 14
                      }}
                    >
                      Edit Customer
                    </Button>
                    {selectedCustomer.status === 'Active' && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleOpenSuspendDialog}
                        sx={{ 
                          fontWeight: 500,
                          fontSize: 14
                        }}
                      >
                        Suspend Account
                      </Button>
                    )}
                  </Stack>
                </Stack>
                
                {/* Customer Summary */}
                <Grid container spacing={3} mb={4}>
                  <Grid item xs={12} md={8}>
                    <UICard>
                      <UICardHeading
                        title="Customer Information"
                        description="Overview of customer account details"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Stack spacing={3}>
                                <Box>
                                  <Typography fontSize={13} color={grey[600]} gutterBottom>
                                    Business Name
                                  </Typography>
                                  <Typography fontSize={15} fontWeight={500}>
                                    {selectedCustomer.name}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography fontSize={13} color={grey[600]} gutterBottom>
                                    Email
                                  </Typography>
                                  <Typography fontSize={15}>
                                    {selectedCustomer.email}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography fontSize={13} color={grey[600]} gutterBottom>
                                    Phone
                                  </Typography>
                                  <Typography fontSize={15}>
                                    {selectedCustomer.phone}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography fontSize={13} color={grey[600]} gutterBottom>
                                    Contact Person
                                  </Typography>
                                  <Typography fontSize={15}>
                                    {selectedCustomer.contactPerson}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Stack spacing={3}>
                                <Box>
                                  <Typography fontSize={13} color={grey[600]} gutterBottom>
                                    Address
                                  </Typography>
                                  <Typography fontSize={15}>
                                    {selectedCustomer.address}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography fontSize={13} color={grey[600]} gutterBottom>
                                    Country
                                  </Typography>
                                  <Typography fontSize={15}>
                                    {selectedCustomer.country}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography fontSize={13} color={grey[600]} gutterBottom>
                                    Plan
                                  </Typography>
                                  <Typography fontSize={15} fontWeight={500}>
                                    {selectedCustomer.plan}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography fontSize={13} color={grey[600]} gutterBottom>
                                    Customer Since
                                  </Typography>
                                  <Typography fontSize={15}>
                                    {new Date(selectedCustomer.createTime).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long', 
                                      day: 'numeric'
                                    })}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Box mt={2}>
                                <Typography fontSize={13} color={grey[600]} gutterBottom>
                                  Notes
                                </Typography>
                                <Typography fontSize={15} sx={{ fontStyle: selectedCustomer.notes ? 'normal' : 'italic' }}>
                                  {selectedCustomer.notes || 'No notes available for this customer.'}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <UICard>
                      <UICardHeading
                        title="Account Summary"
                        description="Current account metrics"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Stack spacing={3}>
                            <Box>
                              <Typography fontSize={13} color={grey[600]} gutterBottom>
                                Total Revenue
                              </Typography>
                              <Typography fontSize={20} fontWeight={600} color={blue[700]}>
                                {selectedCustomer.revenue}
                              </Typography>
                            </Box>
                            
                            <Box>
                              <Typography fontSize={13} color={grey[600]} gutterBottom>
                                Products
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                {selectedCustomer.products.map(product => (
                                  <Chip 
                                    key={product} 
                                    label={product} 
                                    size="small" 
                                    sx={{ fontSize: 12, mb: 0.5 }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                            
                            <Box>
                              <Typography fontSize={13} color={grey[600]} gutterBottom>
                                Status
                              </Typography>
                              <Chip 
                                label={selectedCustomer.status} 
                                size="small" 
                                color={
                                  selectedCustomer.status === 'Active' ? 'success' : 
                                  selectedCustomer.status === 'Pending' ? 'warning' : 
                                  'error'
                                }
                                sx={{ fontSize: 12 }}
                              />
                            </Box>
                            
                            <Box>
                              <Typography fontSize={13} color={grey[600]} gutterBottom>
                                Plan Details
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography fontSize={15} fontWeight={500}>
                                  {selectedCustomer.plan}
                                </Typography>
                                <Button 
                                  size="small" 
                                  onClick={() => navigate(`/resellers/customers/upgrade/${id}`)}
                                  sx={{ 
                                    fontSize: 12, 
                                    minWidth: 0, 
                                    p: '2px 8px', 
                                    textTransform: 'none',
                                    ml: 1
                                  }}
                                >
                                  Upgrade
                                </Button>
                              </Stack>
                            </Box>
                          </Stack>
                        </Box>
                      </UICardContent>
                    </UICard>
                  </Grid>
                </Grid>
                
                {/* Tabs for different customer sections */}
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={handleChangeTab} 
                      aria-label="customer tabs"
                      sx={{
                        '& .MuiTab-root': {
                          fontSize: 14,
                          minHeight: 48,
                          textTransform: 'none',
                          fontWeight: 500
                        }
                      }}
                    >
                      <Tab 
                        icon={<InventoryIcon fontSize="small" />} 
                        iconPosition="start" 
                        label="Services" 
                        id="customer-tab-0"
                        aria-controls="customer-tabpanel-0"
                      />
                      <Tab 
                        icon={<ReceiptLongIcon fontSize="small" />} 
                        iconPosition="start" 
                        label="Invoices" 
                        id="customer-tab-1"
                        aria-controls="customer-tabpanel-1"
                      />
                      <Tab 
                        icon={<HistoryIcon fontSize="small" />} 
                        iconPosition="start" 
                        label="Activity History" 
                        id="customer-tab-2"
                        aria-controls="customer-tabpanel-2"
                      />
                    </Tabs>
                  </Box>
                  
                  {/* Services Tab */}
                  <TabPanel value={tabValue} index={0}>
                    <UICard>
                      <UICardHeading
                        title="Active Services"
                        description="Services currently used by this customer"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            {(selectedCustomer?.services?.length ?? 0) > 0 ? (
                              selectedCustomer?.services?.map((service, index) => (
                                <Grid item xs={12} key={service.id}>
                                  <Box 
                                    sx={{ 
                                      p: 2, 
                                      border: `1px solid ${grey[200]}`,
                                      borderRadius: 1,
                                      mb: index < (selectedCustomer?.services?.length ?? 0) - 1 ? 1 : 0,
                                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.03)'
                                    }}
                                  >
                                    <Grid container alignItems="center">
                                      <Grid item xs={12} md={4}>
                                        <Typography fontWeight={500} fontSize={15}>{service.name}</Typography>
                                        <Typography fontSize={13} color={grey[600]}>ID: {service.id}</Typography>
                                      </Grid>
                                      <Grid item xs={6} md={2}>
                                        <Typography fontSize={13} color={grey[600]}>Product</Typography>
                                        <Chip 
                                          label={service.product} 
                                          size="small" 
                                          sx={{ fontSize: 12, mt: 0.5 }}
                                        />
                                      </Grid>
                                      <Grid item xs={6} md={2}>
                                        <Typography fontSize={13} color={grey[600]}>Status</Typography>
                                        <Chip 
                                          label={service.status} 
                                          size="small" 
                                          color={service.status === 'Active' ? 'success' : 'default'}
                                          sx={{ fontSize: 12, mt: 0.5 }}
                                        />
                                      </Grid>
                                      <Grid item xs={6} md={2}>
                                        <Typography fontSize={13} color={grey[600]}>Usage</Typography>
                                        <Typography fontWeight={500} fontSize={15}>{service.usage}</Typography>
                                      </Grid>
                                      <Grid item xs={6} md={2} sx={{ textAlign: { md: 'right' } }}>
                                        <Utils.ActionButton
                                          variant="outline"
                                          size="small"
                                          onClick={() => console.log('View service details:', service.id)}
                                          sx={{ 
                                            fontSize: 13,
                                            py: 0.5,
                                            px: 1.5
                                          }}
                                        >
                                          Manage
                                        </Utils.ActionButton>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Grid>
                              ))
                            ) : (
                              <Grid item xs={12}>
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                  <Typography fontSize={15} color={grey[600]}>
                                    This customer doesn't have any active services
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>
                  </TabPanel>
                  
                  {/* Invoices Tab */}
                  <TabPanel value={tabValue} index={1}>
                    <UICard>
                      <UICardHeading
                        title="Invoice History"
                        description="Past invoices for this customer"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            {(selectedCustomer?.invoices?.length ?? 0) > 0 ? (
                              selectedCustomer?.invoices?.map((invoice, index) => (
                                <Grid item xs={12} key={invoice.id}>
                                  <Box 
                                    sx={{ 
                                      p: 2, 
                                      border: `1px solid ${grey[200]}`,
                                      borderRadius: 1,
                                      mb: index < (selectedCustomer?.invoices?.length ?? 0) - 1 ? 1 : 0,
                                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.03)'
                                    }}
                                  >
                                    <Grid container alignItems="center">
                                      <Grid item xs={12} md={4}>
                                        <Typography fontWeight={500} fontSize={15}>Invoice {invoice.id}</Typography>
                                        <Typography fontSize={13} color={grey[600]}>
                                          {new Date(invoice.date).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long', 
                                            day: 'numeric'
                                          })}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} md={3}>
                                        <Typography fontSize={13} color={grey[600]}>Amount</Typography>
                                        <Typography fontWeight={500} fontSize={15}>{invoice.amount}</Typography>
                                      </Grid>
                                      <Grid item xs={6} md={3}>
                                        <Typography fontSize={13} color={grey[600]}>Status</Typography>
                                        <Chip 
                                          label={invoice.status} 
                                          size="small" 
                                          color={invoice.status === 'Paid' ? 'success' : invoice.status === 'Pending' ? 'warning' : 'error'}
                                          sx={{ fontSize: 12, mt: 0.5 }}
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={2} sx={{ textAlign: { md: 'right' }, mt: { xs: 1, md: 0 } }}>
                                        <Utils.ActionButton
                                          variant="outline"
                                          size="small"
                                          onClick={() => console.log('View invoice:', invoice.id)}
                                          sx={{ 
                                            fontSize: 13,
                                            py: 0.5,
                                            px: 1.5
                                          }}
                                        >
                                          View Invoice
                                        </Utils.ActionButton>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Grid>
                              ))
                            ) : (
                              <Grid item xs={12}>
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                  <Typography fontSize={15} color={grey[600]}>
                                    No invoices available for this customer
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>
                  </TabPanel>
                  
                  {/* Activity History Tab */}
                  <TabPanel value={tabValue} index={2}>
                    <UICard>
                      <UICardHeading
                        title="Account Activity"
                        description="History of account changes and actions"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Stack spacing={1}>
                            {selectedCustomer?.activity?.map((activity, index) => (
                              <Box 
                                key={index} 
                                sx={{ 
                                  p: 2, 
                                  backgroundColor: index % 2 === 0 ? grey[50] : 'white',
                                  borderRadius: 1
                                }}
                              >
                                <Grid container>
                                  <Grid item xs={12} md={3}>
                                    <Typography fontSize={13} color={grey[600]}>
                                      {new Date(activity.date).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long', 
                                        day: 'numeric'
                                      })}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={7}>
                                    <Typography fontSize={14}>{activity.action}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <Typography fontSize={13} color={grey[600]}>By: {activity.user}</Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </UICardContent>
                    </UICard>
                  </TabPanel>
                </Box>
              </>
            ) : (
              <Alert severity="error">
                Customer not found. This customer may have been deleted or you don't have permission to view it.
              </Alert>
            )}
          </Utils.LayoutUtils.MainWrapper>
        </React.Suspense>
      </Utils.LayoutUtils.MainContainer>

      {/* Suspend Customer Dialog */}
      <Dialog
        open={openSuspendDialog}
        onClose={handleCloseSuspendDialog}
        aria-labelledby="suspend-dialog-title"
        aria-describedby="suspend-dialog-description"
      >
        <DialogTitle id="suspend-dialog-title">
          Suspend Customer
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="suspend-dialog-description">
            Are you sure you want to suspend <strong>{selectedCustomer?.name}</strong>? This will immediately disable their access to all services. You can reactivate the account later.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseSuspendDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSuspendCustomer} 
            sx={{ 
              backgroundColor: red[600],
              color: 'white',
              '&:hover': {
                backgroundColor: red[700],
              },
              textTransform: 'none'
            }}
            variant="contained"
          >
            Suspend Customer
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
} 